/**
 * MobilePlanningHeader — the Plans phone frame's top bar, on every page.
 * Deep routes get Back + title; top-level Plans tabs own their headings, so
 * the bar shows only the mark there. The right end carries the desktop Plans
 * top bar's theme switch and notification bell (audit 2026-11-02, #35; the
 * desktop Plans frame has no Guides, so neither does this one).
 */
import { ChevronLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolvePlanningTitle } from './planningNavConfig';
import { planningMobileBackHref } from './mobilePlanningRoutes';
import { MobileHeaderActions } from './MobileHeaderActions';
import { PrioritiesLogo } from '../components/ui/PrioritiesLogo';

interface MobilePlanningHeaderProps {
  /** A top-level Plans tab: no Back and no title (the page has its own heading). */
  topLevel?: boolean;
}

export function MobilePlanningHeader({ topLevel = false }: MobilePlanningHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const title = resolvePlanningTitle(location.pathname);
  const backHref = planningMobileBackHref(location.pathname);

  return (
    <header className="pa-pt-safe fixed top-0 inset-x-0 z-40 bg-white/90 dark:bg-[color-mix(in_srgb,var(--bg-raised)_90%,transparent)] backdrop-blur-md border-b border-gray-200 dark:border-[var(--rule)]">
      <div className="h-11 px-2 flex items-center gap-1">
        {topLevel ? (
          <span className="h-9 px-1.5 inline-flex items-center flex-shrink-0" aria-hidden>
            <PrioritiesLogo height={22} markOnly />
          </span>
        ) : (
          <button
            type="button"
            onClick={() => {
              if (backHref) navigate(backHref);
              else navigate(-1);
            }}
            aria-label="Back"
            className="h-9 w-9 flex-shrink-0 inline-flex items-center justify-center rounded-full text-gray-600 dark:text-[var(--text-sec)] active:bg-gray-100 dark:active:bg-[var(--hover-tint)]"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {topLevel ? (
          <div className="flex-1" />
        ) : (
          <h1 className="flex-1 min-w-0 font-fieldwork text-sm font-semibold text-gray-900 dark:text-[var(--text-pri)] truncate px-1">
            {title}
          </h1>
        )}
        <MobileHeaderActions />
      </div>
    </header>
  );
}
